import { computed, ref, watch } from 'vue';

import { defineStore } from 'pinia';

import { BEKKI_HISTORY_LIMIT } from '@/composables/api/bekki/constants';
import { getPageContext } from '@/composables/api/bekki/pageContext';
import {
  type BekkiErrorCode,
  type ChatContext,
  type ChatEvent,
  type ChatHistoryEntry,
  type ChatMessage,
  type ChatRequest,
  type DisplayedThinkingStep,
  THINKING_STEP_IDS,
  type ThinkingStep
} from '@/composables/api/bekki/types';
import { useBekkiChatApi } from '@/composables/api/bekki/useBekkiChatApi';
import constants from '@/config/constants';
import { useInternationalization } from '@/i18n';
import router from '@/router';
import { usePermissionsStore } from '@/stores/permissions';
import useScopesStore from '@/stores/scopes';

const TRIMMED_HISTORY_LIMIT = 4;

const createPendingSteps = (): DisplayedThinkingStep[] =>
  THINKING_STEP_IDS.map((id): DisplayedThinkingStep => ({
    id,
    state: 'pending'
  }));

/**
 * The conversation is mirrored to `sessionStorage` so a reload keeps it in the
 * same tab. A new tab or a logout (`resetAllStores` → `$reset`) start empty.
 */
const SESSION_STORAGE_KEY = 'bekkiChat';

interface PersistedConversation {
  conversationId: string;
  messages: ChatMessage[];
  draft: string;
}

const isChatMessage = (value: unknown): value is ChatMessage => {
  if (typeof value !== 'object' || value === null) return false;
  const { id, role, text } = value as ChatMessage;
  return (
    typeof id === 'string' &&
    (role === 'user' || role === 'assistant') &&
    typeof text === 'string'
  );
};

const readPersistedConversation = (): PersistedConversation | null => {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as PersistedConversation).conversationId !== 'string' ||
      !Array.isArray((parsed as PersistedConversation).messages) ||
      !(parsed as PersistedConversation).messages.every(isChatMessage)
    ) {
      return null;
    }
    const { conversationId, messages, draft } = parsed as PersistedConversation;
    return {
      conversationId,
      messages,
      draft: typeof draft === 'string' ? draft : ''
    };
  } catch {
    // Storage blocked or corrupt snapshot — start empty, never crash the app.
    return null;
  }
};

const writePersistedConversation = (snapshot: PersistedConversation): void => {
  try {
    if (!snapshot.messages.length && !snapshot.draft) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Quota / privacy mode — persistence is a convenience, ignore.
  }
};

export const hasMessageContent = (message: ChatMessage): boolean =>
  message.text.trim().length > 0 ||
  (message.charts?.length ?? 0) > 0 ||
  (message.tables?.length ?? 0) > 0;

const toHistory = (source: ChatMessage[], limit: number): ChatHistoryEntry[] =>
  source
    .filter((message) => message.text.trim().length > 0)
    .map(({ role, text }) => ({ role, text }))
    .slice(-limit);

const useBekkiChatStore = defineStore('bekkiChat', () => {
  const scopesStore = useScopesStore();
  const permissionsStore = usePermissionsStore();
  const { currentLanguage } = useInternationalization();
  const api = useBekkiChatApi();

  // State
  const isOpen = ref(false);
  const conversationId = ref<string>(crypto.randomUUID());
  const messages = ref<ChatMessage[]>([]);
  const pendingSteps = ref<DisplayedThinkingStep[]>([]);
  const isStreaming = ref(false);
  const draft = ref('');
  const isFreshConversation = ref(false);
  const activeMessageId = ref<string | null>(null);

  // Persistence — hydrate one microtask late: `piniaResetPlugin` snapshots
  // `$state` synchronously after setup, and `$reset` (logout) must restore the
  // EMPTY conversation, not the one read back from storage.
  queueMicrotask(() => {
    const persisted = readPersistedConversation();
    if (!persisted || isStreaming.value) return;
    conversationId.value = persisted.conversationId;
    messages.value = persisted.messages;
    draft.value = persisted.draft;
  });

  const persistedMessages = computed((): ChatMessage[] =>
    messages.value.filter(
      (message) =>
        message.id !== activeMessageId.value &&
        (message.role === 'user' ||
          hasMessageContent(message) ||
          message.error !== undefined)
    )
  );

  watch(
    [conversationId, persistedMessages, draft],
    () => {
      writePersistedConversation({
        conversationId: conversationId.value,
        messages: persistedMessages.value,
        draft: draft.value
      });
    },
    { deep: true }
  );

  // Getters
  const hasMessages = computed((): boolean => messages.value.length > 0);

  const activeMessage = computed(
    (): ChatMessage | null =>
      messages.value.find((message) => message.id === activeMessageId.value) ??
      null
  );

  // Actions
  const open = (): void => {
    isOpen.value = true;
  };

  const close = (): void => {
    isOpen.value = false;
  };

  const toggle = (): void => {
    isOpen.value = !isOpen.value;
  };

  const clearDraft = (): void => {
    draft.value = '';
  };

  const buildContext = (): ChatContext => {
    const context: ChatContext = {};
    if (currentLanguage.value) {
      context.locale = currentLanguage.value.replace('_', '-');
    }
    const { type, data } = scopesStore.selectedScope;
    const { DEPOT, SUBCONTRACTOR } = constants.scopeTypes;

    if (data) {
      context.tenantId = data.tenant.id;
      context.externalDepotId = data.externalDepotId;
      context.timeZone = data.timeZone;
      if (type === DEPOT) context.dcId = data.id;
    } else if (permissionsStore.selectedTenant) {
      context.tenantId = permissionsStore.selectedTenant;
    }

    if (type === SUBCONTRACTOR && permissionsStore.subcontractorId) {
      context.subcontractorId = permissionsStore.subcontractorId;
    }

    const page = getPageContext(router.currentRoute.value);
    if (page) context.page = page;

    return context;
  };

  const upsertStep = ({ id, state }: ThinkingStep): void => {
    const eventIndex = THINKING_STEP_IDS.indexOf(id);
    pendingSteps.value = pendingSteps.value.map(
      (step): DisplayedThinkingStep => {
        const index = THINKING_STEP_IDS.indexOf(step.id);
        if (index < eventIndex) return { ...step, state: 'done' };
        if (index === eventIndex) return { ...step, state };
        return state === 'active' ? { ...step, state: 'pending' } : step;
      }
    );
  };

  const endTurn = (): void => {
    pendingSteps.value = [];
    activeMessageId.value = null;
    isStreaming.value = false;
  };

  const failTurn = (code: BekkiErrorCode): void => {
    const message = activeMessage.value;
    if (message) message.error = code;
    endTurn();
  };

  const streamTurn = async (
    request: ChatRequest,
    canTrimRetry: boolean
  ): Promise<void> => {
    let isContextTooLong = false;

    const applyEvent = (event: ChatEvent): void => {
      const message = activeMessage.value;
      if (!message) return;

      switch (event.type) {
        case 'start':
          message.id = event.messageId;
          activeMessageId.value = event.messageId;
          break;
        case 'step':
          upsertStep(event.step);
          break;
        case 'delta':
          message.text += event.text;
          break;
        case 'chart':
          message.charts = [...(message.charts ?? []), event.chart];
          break;
        case 'table':
          message.tables = [...(message.tables ?? []), event.table];
          break;
        case 'provenance':
          message.provenance = request.context.timeZone
            ? { ...event.provenance, timeZone: request.context.timeZone }
            : event.provenance;
          break;
        case 'done':
          endTurn();
          break;
        case 'error':
          if (event.code === 'context_too_long' && canTrimRetry) {
            isContextTooLong = true;
            break;
          }
          failTurn(event.code);
          break;
      }
    };

    await api.sendMessage(request, applyEvent);
    if (!isContextTooLong) return;

    const message = activeMessage.value;
    if (message) {
      message.text = '';
      delete message.charts;
      delete message.tables;
    }
    pendingSteps.value = createPendingSteps();
    await streamTurn(
      { ...request, history: request.history.slice(-TRIMMED_HISTORY_LIMIT) },
      false
    );
  };

  const runTurn = async (
    prompt: string,
    history: ChatHistoryEntry[]
  ): Promise<void> => {
    const assistantMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'assistant',
      text: ''
    };
    messages.value.push(assistantMessage);
    activeMessageId.value = assistantMessage.id;
    pendingSteps.value = createPendingSteps();
    isStreaming.value = true;

    await streamTurn(
      {
        conversationId: conversationId.value,
        message: prompt,
        history,
        context: buildContext()
      },
      true
    );
  };

  const send = async (text: string): Promise<void> => {
    const prompt = text.trim();
    if (!prompt || isStreaming.value) return;

    const history = toHistory(messages.value, BEKKI_HISTORY_LIMIT);

    messages.value.push({
      id: crypto.randomUUID(),
      role: 'user',
      text: prompt
    });
    clearDraft();

    await runTurn(prompt, history);
  };

  const retryLast = async (): Promise<void> => {
    const [prompt, failed] = messages.value.slice(-2);
    if (!prompt || !failed?.error) return;

    messages.value.pop();
    const history = toHistory(messages.value.slice(0, -1), BEKKI_HISTORY_LIMIT);

    await runTurn(prompt.text, history);
  };

  /** Keeps `isOpen` and `draft`; everything about the conversation resets. */
  const newConversation = (): void => {
    api.abort();
    endTurn();
    messages.value = [];
    conversationId.value = crypto.randomUUID();
    isFreshConversation.value = true;
  };

  return {
    isOpen,
    conversationId,
    messages,
    pendingSteps,
    isStreaming,
    activeMessageId,
    draft,
    isFreshConversation,
    hasMessages,
    open,
    close,
    toggle,
    newConversation,
    send,
    retryLast,
    clearDraft
  };
});

export default useBekkiChatStore;
